/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useState } from "react";
import { Tag as ITag } from "models/api/response.types";
import Autocomplete, {
  AutocompleteRenderGetTagProps,
  AutocompleteRenderOptionState,
  createFilterOptions,
} from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import { useTheme } from "@mui/material/styles";
import { FilterOptionsState, TextField } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DoneIcon from "@mui/icons-material/Done";
import hexToHSL from "utils/hexToHSL";
import { useTranslation } from "react-i18next";

const filter = createFilterOptions();

// remove accents and other diacritics from a string. Good for comparisons
const norm = (value: string): string => {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
};

const TagInput: React.FC<{
  selectedTags: any[];
  dropDownTags: readonly ITag[];
  disabled: boolean;
  disabledIds?: number[];
  setValue: (value: any[]) => void;
  setAlert?: (value: string) => void;
  handleSaveClick?: () => void;
}> = ({
  selectedTags,
  dropDownTags,
  disabled,
  setValue,
  disabledIds = [],
  setAlert = (message: string) => {
    // eslint-disable-next-line no-console
    console.warn(message);
  },
  handleSaveClick,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [inputValue, setInputValue] = useState("");
  const disabledIdSet = new Set(disabledIds);

  // check if name is a current selected tag
  const nameIsSelected = (name: string): boolean => {
    return selectedTags.some((tag) => norm(tag.name) === norm(name));
  };

  useEffect(() => {
    if (handleSaveClick) handleSaveClick();
  }, [selectedTags]);

  const handleChange = (
    event: React.SyntheticEvent<Element, Event>,
    values: any[]
  ) => {
    const newValue = [...values];
    if (newValue.length > 0) {
      const lastValue = newValue[newValue.length - 1];
      if (!lastValue.id) {
        if (
          selectedTags.some(
            (tag) =>
              tag.name.toLowerCase() === lastValue.inputValue.toLowerCase()
          )
        ) {
          setAlert(t("cannot_create_tag"));
          newValue.splice(-1, 1);
        } else {
          newValue[newValue.length - 1] = {
            color: lastValue.color,
            name: lastValue.inputValue,
            id: -1,
          };
        }
      }
    }
    setValue(newValue);
  };

  const handleInputChange = (
    event: React.SyntheticEvent<Element, Event>,
    newInputValue: string
  ): void => {
    const tokens = newInputValue.split(",");
    if (tokens.length > 1) {
      if (tokens[0] === "") {
        setInputValue("");
        return;
      }
      if (selectedTags.some((tag) => norm(tag.name) === norm(tokens[0]))) {
        setAlert("The tag is already selected");
        setValue(selectedTags);
      } else {
        const matches = dropDownTags.filter(
          (tag) => norm(tag.name) === norm(tokens[0])
        );
        const newValue = [...selectedTags];
        if (matches.length > 0) {
          if (disabledIdSet.has(matches[0].id)) {
            setAlert(t("existing_tags_disabled"));
            setValue(selectedTags);
          } else {
            newValue.push(matches[0]);
          }
        } else {
          newValue.push({ name: tokens[0], id: -1, color: "#D3D3D3" });
        }
        setValue(newValue);
      }
      setInputValue("");
    } else {
      setInputValue(newInputValue);
    }
  };

  const createTagFromInput = (inputText: string) => {
    if (inputText === "") {
      setInputValue("");
      return;
    }
    if (nameIsSelected(inputText)) {
      setAlert("The tag is already selected");
      setValue(selectedTags);
    } else {
      const matches = dropDownTags.filter(
        (tag) => norm(tag.name) === norm(inputText)
      );
      const newValue = [...selectedTags];
      if (matches.length > 0) {
        if (disabledIdSet.has(matches[0].id)) {
          setAlert(t("existing_tags_disabled"));
          setValue(selectedTags);
        } else {
          newValue.push(matches[0]);
        }
      } else {
        newValue.push({ name: inputText, id: -1, color: "#D3D3D3" });
      }
      setValue(newValue);
    }
    setInputValue("");
  };

  const filterOptions = (
    currentOptions: any[],
    state: FilterOptionsState<any>
  ): any[] => {
    const filtered = filter(currentOptions, state);
    const sameName = filtered.filter(
      (tag: any) => norm(tag.name) === norm(state.inputValue)
    );
    if (state.inputValue !== "" && sameName.length === 0) {
      const name = selectedTags.some(
        (tag) => norm(tag.name) === norm(state.inputValue)
      )
        ? `"${state.inputValue}" was selected`
        : `Create Tag: "${state.inputValue}"`;
      filtered.push({
        inputValue: state.inputValue,
        name,
        color: "#D3D3D3",
      });
    }
    return filtered;
  };

  const renderOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: any,
    { selected }: AutocompleteRenderOptionState
  ): React.ReactNode => (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <li {...props}>
      <DoneIcon
        fontSize="small"
        color="action"
        sx={{
          visibility: selected ? "visible" : "hidden",
          marginRight: 2,
        }}
      />
      <Box
        component="span"
        sx={{
          width: 16,
          height: 16,
          flexShrink: 0,
          borderRadius: "0.1rem",
          border: "1px solid rgba(127,127,127,.2)",
          mr: 1,
          mt: "2px",
        }}
        style={{ backgroundColor: option.color }}
      />
      <Box
        sx={{
          flexGrow: 1,
          fontSize: "14px",
          "& span": {
            color: theme.palette.mode === "light" ? "#586069" : "#8b949e",
          },
        }}
      >
        {option.name}
      </Box>
      <CloseIcon
        fontSize="small"
        color="action"
        sx={{
          visibility: selected ? "visible" : "hidden",
        }}
      />
    </li>
  );

  const renderTags = (
    tagValue: any[],
    getTagProps: AutocompleteRenderGetTagProps
  ) =>
    tagValue.map((option, index) => {
      if (option) {
        if (option.color) {
          const { l } = hexToHSL(option.color);
          const color = l < 60 ? "#fff" : "#000";
          return (
            <Chip
              size="medium"
              label={option.name}
              style={{ color, backgroundColor: option.color }}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...getTagProps({ index })}
            />
          );
        }
      }
      return null;
    });

  return (
    <Box sx={{ width: "100%" }}>
      <Autocomplete
        disabled={disabled}
        forcePopupIcon={false}
        multiple
        color="primary"
        clearOnBlur={false}
        disableClearable
        value={selectedTags}
        inputValue={inputValue}
        onChange={handleChange}
        onInputChange={handleInputChange}
        onBlur={() => {
          createTagFromInput(inputValue);
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter") createTagFromInput(inputValue);
        }}
        getOptionDisabled={(option) => disabledIdSet.has(option.id)}
        filterOptions={filterOptions}
        renderOption={renderOption}
        renderTags={renderTags}
        options={
          dropDownTags
            ? [...dropDownTags]
                .filter((tag) => !selectedTags.includes(tag))
                .sort((a, b) => {
                  // Display the selected tags first.
                  let ai = selectedTags.indexOf(a);
                  ai =
                    ai === -1
                      ? selectedTags.length + dropDownTags.indexOf(a)
                      : ai;
                  let bi = selectedTags.indexOf(b);
                  bi =
                    bi === -1
                      ? selectedTags.length + dropDownTags.indexOf(b)
                      : bi;
                  return ai - bi;
                })
            : []
        }
        noOptionsText={t("no_tags")}
        isOptionEqualToValue={(option, value) => option.name === value.name}
        getOptionLabel={(option) => option.name}
        renderInput={(params) => (
          <TextField {...params} autoFocus variant="outlined" size="medium" />
        )}
      />
    </Box>
  );
};

export default TagInput;
